/**
 * Authentication and capabilities.
 *
 * Google sign-in for the owner (FR-AUTH-01). Public self-signup is disabled in the
 * Firebase console (FR-AUTH-02) — there is deliberately no createUser call anywhere
 * in this codebase.
 *
 * Capabilities are read from `users/{uid}` and used to shape the UI. That shaping is
 * presentation only: the same checks run again in `firestore.rules`, and the rules are
 * the authority (FR-AUTH-08).
 */

import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import type { Preferences } from './preferences';

/** The eleven capabilities of FR-AUTH-06, plus user management. */
export const CAPABILITIES = [
  'txn.read',
  'txn.write',
  'txn.delete',
  'loan.read',
  'receivable.read',
  'account.manage',
  'category.manage',
  'budget.manage',
  'report.run',
  'audit.read',
  'backup.run',
  'user.manage',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export type CapabilityMap = Partial<Record<Capability, boolean>>;

export interface UserRecord {
  uid: string;
  displayName: string | null;
  email: string | null;
  capabilities: CapabilityMap;
  preferences: Partial<Preferences>;
  disabled: boolean;
}

export interface AuthState {
  status: 'loading' | 'signed-out' | 'signed-in' | 'no-record';
  user: User | null;
  record: UserRecord | null;
}

export const INITIAL_AUTH_STATE: AuthState = {
  status: 'loading',
  user: null,
  record: null,
};

const provider = new GoogleAuthProvider();

/**
 * True when running as an installed PWA rather than in a browser tab.
 *
 * Home-screen PWAs — which FR-PLT-04 requires and FR-MSG-07 assumes on iOS — routinely
 * block or mishandle `window.open`, so a popup sign-in can simply do nothing there. The
 * button appears to be broken with no error to explain it.
 */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** Popup failures that mean "this environment cannot do popups", not "the user declined". */
const REDIRECT_WORTHY = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
  'auth/internal-error',
]);

/**
 * Sign in with Google.
 *
 * Popup first — it keeps the user on the page and avoids the full-page round trip — with
 * a redirect fallback for environments where popups do not work. An installed PWA skips
 * straight to redirect rather than trying a popup that will fail silently.
 *
 * `auth/popup-closed-by-user` and `auth/cancelled-popup-request` are deliberately NOT in
 * the fallback set: those mean the user changed their mind, and bouncing them through a
 * full-page redirect after they closed the popup would be hostile.
 */
export async function signInWithGoogle(): Promise<void> {
  if (isStandalone()) {
    await signInWithRedirect(auth, provider);
    return;
  }

  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    const code = (error as { code?: string }).code ?? '';
    if (REDIRECT_WORTHY.has(code)) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw error;
  }
}

/**
 * Collect the result of a redirect sign-in on page load.
 *
 * Returns the error rather than throwing, so a stale or abandoned redirect cannot stop
 * the app rendering. `onAuthStateChanged` reports the session either way; this exists
 * only to surface a *failed* redirect, which otherwise leaves the user on a sign-in
 * screen with no explanation of what went wrong.
 */
export async function completeRedirectSignIn(): Promise<Error | null> {
  try {
    await getRedirectResult(auth);
    return null;
  } catch (error) {
    return error instanceof Error ? error : new Error(String(error));
  }
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

function toRecord(uid: string, data: Record<string, unknown>): UserRecord {
  return {
    uid,
    displayName: (data.displayName as string) ?? null,
    email: (data.email as string) ?? null,
    capabilities: (data.capabilities as CapabilityMap) ?? {},
    preferences: (data.preferences as Partial<Preferences>) ?? {},
    disabled: data.disabled === true,
  };
}

/**
 * Subscribe to sign-in state and the user's capability record together.
 *
 * `no-record` is a real state, not an error: someone can hold a valid Google session
 * while having no `users/{uid}` document — which is exactly what happens to anyone who
 * is not the owner. They are authenticated and entitled to nothing, and the app should
 * say so plainly rather than showing an empty dashboard.
 */
export function watchAuth(onChange: (state: AuthState) => void): () => void {
  let unsubscribeRecord: (() => void) | null = null;

  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    unsubscribeRecord?.();
    unsubscribeRecord = null;

    if (!user) {
      onChange({ status: 'signed-out', user: null, record: null });
      return;
    }

    unsubscribeRecord = onSnapshot(
      doc(db, 'users', user.uid),
      (snapshot) => {
        if (!snapshot.exists()) {
          onChange({ status: 'no-record', user, record: null });
          return;
        }
        onChange({
          status: 'signed-in',
          user,
          record: toRecord(user.uid, snapshot.data()),
        });
      },
      () => onChange({ status: 'no-record', user, record: null }),
    );
  });

  return () => {
    unsubscribeRecord?.();
    unsubscribeAuth();
  };
}

export async function fetchUserRecord(uid: string): Promise<UserRecord | null> {
  const snapshot = await getDoc(doc(db, 'users', uid));
  return snapshot.exists() ? toRecord(uid, snapshot.data()) : null;
}

export function can(record: UserRecord | null, capability: Capability): boolean {
  return record?.disabled === false && record.capabilities[capability] === true;
}
